import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from '@utils/framerMotionProxy';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { reviewsAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import ReviewModal from '../ReviewModal/ReviewModal';
import { getImageUrl } from '../../utils/urls';

const ReviewsSection = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDirection, setPageDirection] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateViewport = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 1024);
      }
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const { data: reviewsData, isLoading, refetch } = useQuery(
    ['reviews'],
    () => reviewsAPI.getReviews(),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const reviews = reviewsData?.data?.data || [];
  const itemsPerPage = isMobile ? 6 : 8;
  const totalPages = Math.max(1, Math.ceil(reviews.length / itemsPerPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedReviews = reviews.slice(
    (currentPageSafe - 1) * itemsPerPage,
    currentPageSafe * itemsPerPage
  );
  const reviewPlaceholders = Array.from(
    { length: Math.max(0, itemsPerPage - paginatedReviews.length) },
    (_, index) => `review-placeholder-${currentPageSafe}-${index}`
  );

  useEffect(() => {
    setPageDirection(0);
    setCurrentPage(1);
  }, [isMobile, reviews.length]);

  const pageVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '130%' : '-130%',
    }),
    center: {
      x: '0%',
    },
    exit: (direction) => ({
      x: direction > 0 ? '-130%' : '130%',
    }),
  };

  const handleNextPage = () => {
    setPageDirection(1);
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePrevPage = () => {
    setPageDirection(-1);
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const canGoPrev = totalPages > 1 && currentPageSafe > 1;
  const canGoNext = totalPages > 1 && currentPageSafe < totalPages;

  const handleReviewSubmitted = () => {
    setShowReviewModal(false);
    refetch();
  };

  const handleAddReviewClick = () => {
    if (isAuthenticated) {
      setShowReviewModal(true);
      return;
    }

    navigate('/auth', { state: { backgroundLocation: location } });
  };

  const renderStars = (rating) => (
    Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={`w-3.5 h-3.5 ${index < rating ? 'text-white/75' : 'text-white/35'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))
  );

  return (
    <>
      <section
        id="reviews"
        className="section"
      >
        <div className="container-custom">
          <div className="max-w-6xl mx-auto mb-6 grid grid-cols-3 items-center gap-2 min-h-9">
            <div className="justify-self-start">
              <button
                onClick={handlePrevPage}
                disabled={!canGoPrev}
                className={`inline-flex items-center text-sm transition-opacity ${
                  canGoPrev
                    ? 'text-gray-600 hover:text-gray-900 opacity-100'
                    : 'text-gray-600 opacity-0 pointer-events-none'
                }`}
              >
                ← {t('common.back_short', { defaultValue: 'Назад' })}
              </button>
            </div>

            <div className="justify-self-center text-center">
              <div className="text-xl md:text-2xl lg:text-[1.8rem] uppercase font-bold text-gray-900 text-center">
                {t('reviews.title')}
              </div>
            </div>

            <div className="justify-self-end">
              <button
                onClick={handleNextPage}
                disabled={!canGoNext}
                className={`inline-flex items-center text-sm transition-opacity ${
                  canGoNext
                    ? 'text-gray-600 hover:text-gray-900 opacity-100'
                    : 'text-gray-600 opacity-0 pointer-events-none'
                }`}
              >
                {t('common.more_short', { defaultValue: 'Еще' })} →
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loader" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <p className="text-2xl text-gray-600 mb-4">{t('reviews.empty_message')}</p>
                <p className="text-gray-500 text-sm">{t('reviews.be_first')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="max-w-6xl mx-auto overflow-hidden">
                <AnimatePresence initial={false} custom={pageDirection} mode="popLayout">
                  <motion.div
                    key={currentPageSafe}
                    custom={pageDirection}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
                    style={{ willChange: 'transform' }}
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
                  >
                    {paginatedReviews.map((review) => (
                      <div
                        key={review.id}
                        className="cursor-pointer flex flex-col"
                        onClick={() => navigate(`/reviews/${review.id}`)}
                      >
                        <div className="relative rounded-2xl overflow-hidden aspect-square bg-gray-50 mb-3">
                          {review.image ? (
                            <img
                              src={getImageUrl(`/uploads/${review.image}`)}
                              alt={`Отзыв от ${review.userName}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-full h-full relative"
                              style={{
                                backgroundColor: '#EC8C8E'
                              }}
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-white text-[10px] font-medium uppercase tracking-wide">
                                  {t('reviews.noPhoto', { defaultValue: 'без изображения' })}
                                </span>
                              </div>
                            </div>
                          )}
                          {review.rating && (
                            <div className="absolute right-2 bottom-2 flex items-center justify-end gap-0.5 pointer-events-none">
                              {renderStars(review.rating)}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 flex flex-col gap-2 text-left items-start">
                          <h3 className="text-[13px] font-medium text-gray-900 leading-snug">{review.userName}</h3>
                        </div>
                      </div>
                    ))}

                    {reviewPlaceholders.map((placeholderKey) => (
                      <div
                        key={placeholderKey}
                        className="flex flex-col pointer-events-none"
                        aria-hidden="true"
                      >
                        <div className="relative rounded-2xl overflow-hidden aspect-square bg-gray-50 mb-3 opacity-0" />
                        <div className="flex-1 flex flex-col gap-2 text-left items-start">
                          <h3 className="text-[13px] font-medium leading-snug opacity-0">placeholder</h3>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

            </>
          )}

          <div className="text-center mt-6">
            <button
              onClick={handleAddReviewClick}
              className="inline-flex items-center px-4 py-1.5 text-sm text-white font-medium rounded-full transition-all duration-300 hover:opacity-90 uppercase"
              style={{ backgroundColor: '#EC8C8E' }}
            >
              {t('reviews.addReview')}
            </button>
          </div>

        </div>
      </section>

      <AnimatePresence>
        {showReviewModal && (
          <ReviewModal onClose={() => setShowReviewModal(false)} onSubmitted={handleReviewSubmitted} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ReviewsSection;
